import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Call as VoiceCall, Device } from '@twilio/voice-sdk';
import { ApiError, api } from './api';
import { useAuth } from './auth';

/**
 * The browser as one of the company's phones.
 *
 * Holds a single Twilio `Device` for the tab, registered under this user's
 * identity, so an inbound call rings here and a click-to-call goes out from
 * here. Everything that shows a call — the banner, the on-call bar, a phone
 * number that dials itself — reads this rather than touching the SDK.
 *
 * Three things make it survive a real shift:
 *
 *   1. **The SDK is loaded on demand.** It is a large bundle that only office
 *      users need, and importing it at the top would also put it in every
 *      server render the harness does, where there is no WebRTC at all.
 *   2. **The token is refreshed before it lapses.** A dispatcher whose token
 *      expired mid-afternoon stops ringing without any sign of it — Twilio
 *      just routes past them to the forwarding number. `tokenWillExpire`
 *      fires a minute early and the new token is swapped in place.
 *   3. **"No number" is a state, not an error.** A company that has not bought
 *      a number gets a 422 from the token route, and the softphone quietly
 *      stays off; click-to-call falls back to the bridge in CallActions.
 */

export type SoftphoneStatus = 'off' | 'connecting' | 'ready' | 'error';

export interface IncomingCall {
  /** The caller's number, as Twilio presents it. */
  from: string;
  /** The parent call's sid, so the banner can find the screen-pop's row. */
  callSid: string | null;
}

export interface ActiveCall {
  peer: string;
  direction: 'inbound' | 'outbound';
  /** When the other side picked up. Null while an outbound call is ringing. */
  connectedAt: number | null;
  muted: boolean;
}

export interface SoftphoneState {
  status: SoftphoneStatus;
  incoming: IncomingCall | null;
  active: ActiveCall | null;
  /** Why the last action failed, in words a dispatcher can act on. */
  error: string | null;
  answer: () => void;
  decline: () => void;
  hangup: () => void;
  toggleMute: () => void;
  dial: (toNumber: string) => Promise<void>;
  clearError: () => void;
}

const SoftphoneContext = createContext<SoftphoneState | null>(null);

/** How early the SDK warns before the token expires. */
const TOKEN_REFRESH_MS = 60_000;

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { user, status: authStatus } = useAuth();
  const isOffice = user?.role === 'admin' || user?.role === 'dispatcher';

  const [status, setStatus] = useState<SoftphoneStatus>('off');
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);
  const [error, setError] = useState<string | null>(null);

  const device = useRef<Device | null>(null);
  const ringing = useRef<VoiceCall | null>(null);
  const current = useRef<VoiceCall | null>(null);

  /** Wire a call that is now ours — answered, or placed — to the bar. */
  const adopt = useCallback((call: VoiceCall, direction: 'inbound' | 'outbound', peer: string) => {
    current.current = call;
    setActive({ peer, direction, connectedAt: direction === 'inbound' ? Date.now() : null, muted: false });

    call.on('accept', () =>
      setActive((a) => (a ? { ...a, connectedAt: a.connectedAt ?? Date.now() } : a)),
    );
    call.on('mute', (muted: boolean) => setActive((a) => (a ? { ...a, muted } : a)));

    const end = () => {
      if (current.current !== call) return;
      current.current = null;
      setActive(null);
    };
    call.on('disconnect', end);
    call.on('cancel', end);
    call.on('reject', end);
    call.on('error', (e: { message?: string }) => {
      setError(e.message ?? 'The call dropped');
      end();
    });
  }, []);

  useEffect(() => {
    if (authStatus !== 'authenticated' || !isOffice) return;
    let cancelled = false;

    const start = async () => {
      setStatus('connecting');

      let token: string;
      try {
        token = (await api.telephony.token()).token;
      } catch (e) {
        if (cancelled) return;
        // No number yet, or a phone system that is not switched on.
        /*
         * 403 as well as 422 and 503. The platform owner can switch telephony
         * off per company (auth/telephony-enabled.ts), and that refusal is a
         * 403 — a company that simply does not have the phone is "off", not a
         * fault to report to a dispatcher who cannot do anything about it.
         */
        const expected = e instanceof ApiError && [403, 422, 503].includes(e.status);
        setStatus(expected ? 'off' : 'error');
        return;
      }

      const { Device: TwilioDevice } = await import('@twilio/voice-sdk');
      if (cancelled) return;

      const d = new TwilioDevice(token, {
        tokenRefreshMs: TOKEN_REFRESH_MS,
        // A tab closed mid-call drops the customer; the browser asks first.
        closeProtection: true,
      });
      device.current = d;

      d.on('registered', () => !cancelled && setStatus('ready'));
      d.on('unregistered', () => !cancelled && setStatus('connecting'));
      d.on('error', (e: { message?: string }) => {
        if (cancelled) return;
        setError(e.message ?? 'The phone connection failed');
      });

      d.on('tokenWillExpire', async () => {
        try {
          d.updateToken((await api.telephony.token()).token);
        } catch {
          /*
           * Left to expire rather than torn down: the next refresh attempt
           * may well succeed, and an unregistered device is no worse than a
           * torn-down one.
           */
        }
      });

      d.on('incoming', (call: VoiceCall) => {
        // One at a time. A second caller is still ringing everybody else.
        if (ringing.current || current.current) {
          call.reject();
          return;
        }
        ringing.current = call;
        setIncoming({
          from: call.parameters['From'] ?? '',
          callSid: call.customParameters.get('callSid') ?? null,
        });

        const gone = () => {
          if (ringing.current !== call) return;
          ringing.current = null;
          setIncoming(null);
        };
        // Somebody else answered, the caller hung up, or this tab declined.
        call.on('cancel', gone);
        call.on('disconnect', gone);
        call.on('reject', gone);
        call.on('accept', gone);
      });

      try {
        await d.register();
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    void start();

    return () => {
      cancelled = true;
      device.current?.destroy();
      device.current = null;
      ringing.current = null;
      current.current = null;
      setIncoming(null);
      setActive(null);
      setStatus('off');
    };
  }, [authStatus, isOffice]);

  const answer = useCallback(() => {
    const call = ringing.current;
    if (!call) return;
    call.accept();
    adopt(call, 'inbound', call.parameters['From'] ?? '');
  }, [adopt]);

  /*
   * `reject`, not `ignore`. Ignoring only silences this tab and leaves the
   * caller ringing out the full timeout; rejecting tells Twilio this browser
   * is not taking it, so the call moves on to whoever else is ringing — or,
   * when it was the only one, straight to the forwarding number.
   */
  const decline = useCallback(() => ringing.current?.reject(), []);

  const hangup = useCallback(() => current.current?.disconnect(), []);

  const toggleMute = useCallback(() => {
    const call = current.current;
    if (call) call.mute(!call.isMuted());
  }, []);

  const dial = useCallback(
    async (toNumber: string) => {
      const d = device.current;
      if (!d || current.current) return;
      try {
        /*
         * Only `To`. The caller ID is the company's own number and is chosen
         * by the server — see outboundTwiml. Nothing the browser sends can
         * change it, and nothing here tries to.
         */
        const call = await d.connect({ params: { To: toNumber } });
        adopt(call, 'outbound', toNumber);
      } catch (e) {
        setError((e as { message?: string }).message ?? 'Could not place the call');
      }
    },
    [adopt],
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<SoftphoneState>(
    () => ({ status, incoming, active, error, answer, decline, hangup, toggleMute, dial, clearError }),
    [status, incoming, active, error, answer, decline, hangup, toggleMute, dial, clearError],
  );

  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}

export function useSoftphone(): SoftphoneState {
  const ctx = useContext(SoftphoneContext);
  // Outside the provider — a technician's session — it is simply off.
  return (
    ctx ?? {
      status: 'off',
      incoming: null,
      active: null,
      error: null,
      answer: () => {},
      decline: () => {},
      hangup: () => {},
      toggleMute: () => {},
      dial: async () => {},
      clearError: () => {},
    }
  );
}
